import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface CopyButtonProps {
  value: string;
  label?: string;
  tooltip?: string;
  variant?: 'ghost' | 'outline' | 'secondary';
  size?: 'sm' | 'default' | 'icon';
  className?: string;
}

export function CopyButton({ value, label, tooltip = 'Copy', variant = 'ghost', size = 'sm', className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Fallback for environments without clipboard API
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const btn = (
    <Button
      variant={variant}
      size={size === 'icon' ? 'icon' : size}
      onClick={handleCopy}
      className={cn('shrink-0', className)}
      aria-label={tooltip}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-500" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
      {label && !size.includes('icon') && (
        <span className="ml-1.5">{copied ? 'Copied!' : label}</span>
      )}
    </Button>
  );

  if (!tooltip) return btn;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {copied ? 'Copied!' : tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
